/*  MUI  */
import { Box, List } from '@mui/material';

/*  Shared  */
import { FontSizes, URLs } from 'application/Shared/Constants';
import { SessionHelper } from 'application/Shared/Helpers';
import { Session } from 'application/Shared/types/session';

/*  React  */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/*  Components  */
import { Tab } from '../../Components/Tab';
import { IconForSidebarListItem, IconForTab } from './SidebarIcons';
import { SidebarListItem } from './SidebarListItem';

/**
 * Component that renders the sidebar navigation links for partner users
 * @param {Object} props - Component props
 * @param {string} [props.hubType] - Type of partner hub (e.g. 'Premium')
 * @param {string} [props.page] - Current active page identifier
 * @param {Object} props.session - User session object containing authentication and permissions data
 * @returns {JSX.Element} Sidebar navigation list with partner-specific links and tabs
 */
export const SidebarPartnerLinks = ({
    hubType,
    page,
    session
}: {
    hubType?: string;
    page?: string;
    session: Session;
}): JSX.Element => {
    const { t } = useTranslation();
    const { partnerId, tab } = useParams();

    const sessionHelper = new SessionHelper(session);

    // For pages like a partner user's 'configureSelfUser' page, this component will be shown since they're
    // a partner tenant, so we can use the session's tenant ID as a fallback to the clientId.
    const partnerIdForLinks = partnerId || sessionHelper.getTenantId();

    const selectAlerts = page === 'partner_alerts';

    const selectOrgPreferences =
        page === 'partner_users' ||
        page === 'partner_integrations' ||
        page === 'partner_utilities';

    const selectSalesSupport =
        page === 'partner_sales_hub' || page === 'partner_settings';

    const selectReports =
        page === 'partner_availablePartnerReports' ||
        page === 'partner_availableAdminReports' ||
        page === 'partner_scheduledReports';

    return (
        <List
            aria-labelledby="nested-list-subheader"
            component="nav"
            style={{ display: 'flex', flexDirection: 'column' }}
        >
            <Box data-testid="sidebarPartnerLinks">
                <SidebarListItem
                    icon={<IconForSidebarListItem src="Apps" />}
                    id="partner_dashboard"
                    name={t('Common.dashboard')}
                    permission={['partner', 'system_admin']}
                    route={`/partner/${partnerIdForLinks}/dashboard`}
                    selected={page === 'partner_dashboard'}
                />
                <SidebarListItem
                    icon={<IconForSidebarListItem src="Users" />}
                    id="partner_clients"
                    name={t('Common.clients')}
                    permission={['partner', 'system_admin']}
                    route={`/partner/${partnerIdForLinks}/organizations`}
                    selected={page === 'partner_organizations'}
                />
                <SidebarListItem
                    icon={<IconForSidebarListItem src="LibraryBook" />}
                    id="partner_apps"
                    name={t('Common.application_stack')}
                    permission={['partner', 'system_admin']}
                    route={`/partner/${partnerIdForLinks}/applicationSettings`}
                    selected={page === 'partner_application_settings'}
                />
                <SidebarListItem
                    icon={<IconForSidebarListItem src="WarningTriangle" />}
                    id="partner_vendorIncidents"
                    name={t('Common.vendor_incidents')}
                    permission={['partner', 'system_admin']}
                    route={`/partner/${partnerIdForLinks}/breaches`}
                    selected={page === 'partner_breaches'}
                />
                <Tab
                    icon={<IconForTab src="BarChart" />}
                    id="partner_reports"
                    name={t('Common.reports')}
                    permission={['partner', 'system_admin']}
                    route={`/partner/${partnerIdForLinks}/reports/adminReportSelection`}
                    selected={selectReports}
                    subTabs={
                        <>
                            <Tab
                                hidden={!selectReports}
                                id="partner_reports_availableReports_admin"
                                name={t('Reports.admin')}
                                permission={['partner', 'system_admin']}
                                route={`/partner/${partnerIdForLinks}/reports/adminReportSelection`}
                                selected={
                                    page === 'partner_availableAdminReports'
                                }
                                showNewBadge
                                subTab
                            />
                            <Tab
                                hidden={!selectReports}
                                id="partner_reports_availableReports"
                                name={t('Reports.partner')}
                                permission={['partner', 'system_admin']}
                                route={`/partner/${partnerIdForLinks}/reports/partnerReportSelection`}
                                selected={
                                    page === 'partner_availablePartnerReports'
                                }
                                subTab
                            />
                            <Tab
                                hidden={!selectReports}
                                id="partner_reports_scheduledReports"
                                name={t('Reports.scheduledReports')}
                                permission={['partner', 'system_admin']}
                                route={`/partner/${partnerIdForLinks}/reports/scheduledReports`}
                                selected={page === 'partner_scheduledReports'}
                                subTab
                            />
                        </>
                    }
                    variant="sidebarMainTab"
                />
                <SidebarListItem
                    icon={<IconForSidebarListItem src="Subscription" />}
                    id="partner_contracts"
                    name={t('Common.contract_management')}
                    permission={['partner', 'system_admin']}
                    route={`/partner/${partnerIdForLinks}/contracts`}
                    selected={page === 'partner_contracts'}
                />
                <Tab
                    icon={<IconForTab src="Hierarchy" />}
                    id="partner_orgPreferences"
                    name={t('Common.org_preferences')}
                    permission={['partner']}
                    route={`/partner/${partnerIdForLinks}/manageUsers/users`}
                    selected={selectOrgPreferences}
                    subTabs={
                        <>
                            <Tab
                                hidden={!selectOrgPreferences}
                                id="partner_manageUsers"
                                name={t('Common.manage_users')}
                                permission={['partner', 'system_permissions']}
                                selected={page === 'partner_users'}
                                route={`/partner/${partnerIdForLinks}/manageUsers/users`}
                                subTab
                            />
                            <Tab
                                hidden={!selectOrgPreferences}
                                id="partner_integrations"
                                name={t('Common.integrations')}
                                permission={['partner', 'system_admin']}
                                route={`/partner/${partnerIdForLinks}/integrations/partnerLevel`}
                                selected={page === 'partner_integrations'}
                                subTab
                            />
                            <Tab
                                hidden={!selectOrgPreferences}
                                id="partner_utilities"
                                name={t('Common.utilities')}
                                permission={['partner']}
                                selected={page === 'partner_utilities'}
                                route={`/partner/${partnerIdForLinks}/configure`}
                                subTab
                            />
                        </>
                    }
                    variant="sidebarMainTab"
                />
                <Tab
                    icon={<IconForTab src="Alerts" size={FontSizes[1]} />}
                    id="partner_alerting"
                    name={t('Common.alerting')}
                    permission="system_admin"
                    route={`/partner/${partnerIdForLinks}/alerts/history`}
                    selected={selectAlerts}
                    subTabs={
                        <>
                            <Tab
                                hidden={!selectAlerts}
                                id="partner_alertingHistory"
                                name={t('Alerts.alertingHistory')}
                                permission="system_admin"
                                route={`/partner/${partnerIdForLinks}/alerts/history`}
                                selected={
                                    page === 'partner_alerts' &&
                                    tab === 'history'
                                }
                                subTab
                            />
                            <Tab
                                hidden={!selectAlerts}
                                id="partner_alertCreation"
                                name={t('Alerts.alertCreation')}
                                permission="system_admin"
                                route={`/partner/${partnerIdForLinks}/alerts/creation`}
                                selected={
                                    page === 'partner_alerts' &&
                                    tab === 'creation'
                                }
                                subTab
                            />
                        </>
                    }
                    variant="sidebarMainTab"
                />
                <Tab
                    icon={<IconForTab src="Store" />}
                    id="partner_salesAndSupport"
                    name={t('Common.sales_support')}
                    permission={['partner']}
                    route={`/partner/${partnerIdForLinks}/hubSettings/branding`}
                    selected={selectSalesSupport}
                    subTabs={
                        <>
                            <Tab
                                hidden={!selectSalesSupport}
                                id="partner_hubSettings"
                                name={t('Common.hub_settings')}
                                permission={[
                                    'partner',
                                    'system_admin',
                                    'system_permissions'
                                ]}
                                route={`/partner/${partnerIdForLinks}/hubSettings/branding`}
                                selected={page === 'partner_settings'}
                                subTab
                            />
                            <Tab
                                hidden={!selectSalesSupport}
                                id="partner_salesDocs"
                                name={t('Common.sales_hub')}
                                operator="or"
                                permission={[
                                    'system_admin',
                                    'partner_sales_hub'
                                ]}
                                route={`/partner/${partnerIdForLinks}/salesHub`}
                                selected={page === 'partner_sales_hub'}
                                subTab
                            />
                            <Tab
                                hidden={
                                    hubType !== 'Premium' || !selectSalesSupport
                                }
                                id="partner_demoInstance"
                                name={t('Sidebar.demo_environment')}
                                permission={['partner']}
                                route="https://demo.saas.auvik.com/"
                                subTab
                            />
                            <Tab
                                hidden={!selectSalesSupport}
                                id="partner_supportCenter"
                                name={t('Common.support_center')}
                                permission={['partner']}
                                route={URLs.support}
                                subTab
                            />
                        </>
                    }
                    variant="sidebarMainTab"
                />
            </Box>
        </List>
    );
};
